import { Col, Tag, Typography, Popover, Button, message } from "antd";
import { EyeOutlined, CopyOutlined } from "@ant-design/icons";
import { caSyncErrorsService } from "../../../../shared/services";
import { TableColumns } from "../../../../shared/components/ui/Table/types";
import Table from "../../../../shared/components/ui/Table";
import moment from "moment";

const { Text, Title } = Typography;

interface CaSyncError {
  id: number;
  error_type: string;
  error_message: string;
  error_code: string | null;
  sync_direction: string;
  branch: string;
  external_reference_id: string | null;
  local_reference_id: number | null;
  error_context: any;
  stack_trace: string | null;
  retry_count: number;
  sync_job_id: string | null;
  createdAt: string;
  updatedAt: string;
}

export const TableCaSyncErrors = () => {
  const columns: TableColumns<CaSyncError> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 2,
      sorter: true,
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
      render: (_, item) => {
        return (
          <Col
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <Text strong>#{item?.id}</Text>
          </Col>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "error_type",
      key: "error_type",
      width: 4,
      render: (_, item) => {
        const getColor = (type: string) => {
          switch (type) {
            case "STUDENT_SYNC":
              return "blue";
            case "PAYMENT_SYNC":
              return "orange";
            case "PROGRAM_NOT_FOUND":
              return "red";
            case "DUPLICATE_EMAIL":
            case "DUPLICATE_PHONE":
              return "purple";
            case "DATABASE_ERROR":
              return "volcano";
            case "VALIDATION_ERROR":
              return "gold";
            case "EXTERNAL_DB_CONNECTION":
              return "magenta";
            default:
              return "default";
          }
        };

        return (
          <Col
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <Tag color={getColor(item?.error_type)}>{item?.error_type}</Tag>
          </Col>
        );
      },
      filters: [
        { text: "STUDENT_SYNC", value: "STUDENT_SYNC" },
        { text: "PAYMENT_SYNC", value: "PAYMENT_SYNC" },
        { text: "PROGRAM_NOT_FOUND", value: "PROGRAM_NOT_FOUND" },
        { text: "DUPLICATE_EMAIL", value: "DUPLICATE_EMAIL" },
        { text: "DUPLICATE_PHONE", value: "DUPLICATE_PHONE" },
        { text: "DATABASE_ERROR", value: "DATABASE_ERROR" },
        { text: "VALIDATION_ERROR", value: "VALIDATION_ERROR" },
        { text: "EXTERNAL_DB_CONNECTION", value: "EXTERNAL_DB_CONNECTION" },
        { text: "OTHER", value: "OTHER" },
      ],
    },
    {
      title: "Direction",
      dataIndex: "sync_direction",
      key: "sync_direction",
      width: 3,
      render: (_, item) => {
        return (
          <Col
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <Tag color={item?.sync_direction === "TO_EXTERNAL" ? "green" : "cyan"}>
              {item?.sync_direction}
            </Tag>
          </Col>
        );
      },
      filters: [
        { text: "TO_EXTERNAL", value: "TO_EXTERNAL" },
        { text: "FROM_EXTERNAL", value: "FROM_EXTERNAL" },
      ],
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branch",
      width: 3,
      render: (_, item) => {
        return (
          <Col
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <Text>{item?.branch}</Text>
          </Col>
        );
      },
    },
    {
      title: "Error Message",
      dataIndex: "error_message",
      key: "error_message",
      width: 8,
      render: (_, item) => {
        const handleCopyError = () => {
          const fullError = `Error Type: ${item?.error_type}
Sync Direction: ${item?.sync_direction}
Branch: ${item?.branch}
Error Message: ${item?.error_message}
${item?.error_code ? `Error Code: ${item?.error_code}` : ''}
${item?.stack_trace ? `\nStack Trace:\n${item?.stack_trace}` : ''}
${item?.error_context ? `\nContext:\n${JSON.stringify(item?.error_context, null, 2)}` : ''}`;

          navigator.clipboard.writeText(fullError).then(() => {
            message.success('Error copied to clipboard!');
          }).catch(() => {
            message.error('Failed to copy error');
          });
        };

        const errorContent = (
          <div style={{ maxWidth: 600, maxHeight: 400, overflow: "auto" }}>
            <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text strong>Error Message:</Text>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopyError}
                title="Copy full error"
              >
                Copy
              </Button>
            </div>
            <div style={{ marginBottom: 12, padding: 12, background: "#f5f5f5", borderRadius: 4 }}>
              <Text style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>
                {item?.error_message}
              </Text>
            </div>
            {item?.error_code && (
              <div style={{ marginBottom: 12 }}>
                <Text strong>Error Code:</Text>
                <div style={{ marginTop: 8 }}>
                  <Tag color="red">{item?.error_code}</Tag>
                </div>
              </div>
            )}
            {item?.stack_trace && (
              <div style={{ marginBottom: 12 }}>
                <Text strong>Stack Trace:</Text>
                <div style={{ marginTop: 8, padding: 12, background: "#f5f5f5", borderRadius: 4, fontFamily: "monospace" }}>
                  <Text style={{ whiteSpace: "pre-wrap", fontSize: "0.75rem" }}>
                    {item?.stack_trace}
                  </Text>
                </div>
              </div>
            )}
            {item?.error_context && (
              <div>
                <Text strong>Context:</Text>
                <div style={{ marginTop: 8, padding: 12, background: "#f5f5f5", borderRadius: 4, fontFamily: "monospace" }}>
                  <pre style={{ margin: 0, fontSize: "0.75rem" }}>
                    {JSON.stringify(item?.error_context, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        );

        return (
          <Col
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div style={{ flex: 1 }}>
              <Text style={{ fontSize: "0.85rem" }}>
                {item?.error_message?.substring(0, 100)}
                {item?.error_message?.length > 100 && "..."}
              </Text>
              {item?.error_code && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                    Code: {item?.error_code}
                  </Text>
                </div>
              )}
            </div>
            <Popover
              content={errorContent}
              title="Error Details"
              trigger="click"
              placement="left"
              overlayStyle={{ maxWidth: 650 }}
            >
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                style={{ flexShrink: 0 }}
              />
            </Popover>
          </Col>
        );
      },
    },
    {
      title: "References",
      key: "references",
      width: 4,
      render: (_, item) => {
        return (
          <Col
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {item?.external_reference_id && (
              <Text style={{ fontSize: "0.75rem" }}>
                Ext: {item?.external_reference_id}
              </Text>
            )}
            {item?.local_reference_id && (
              <Text style={{ fontSize: "0.75rem" }}>
                Local: {item?.local_reference_id}
              </Text>
            )}
            {item?.sync_job_id && (
              <Text type="secondary" style={{ fontSize: "0.7rem" }}>
                Job: {item?.sync_job_id}
              </Text>
            )}
          </Col>
        );
      },
    },
    {
      title: "Retry Count",
      dataIndex: "retry_count",
      key: "retry_count",
      width: 3,
      render: (_, item) => {
        return (
          <Col
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <Tag color={item?.retry_count > 0 ? "warning" : "default"}>
              {item?.retry_count}
            </Tag>
          </Col>
        );
      },
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 4,
      render: (_, item) => {
        return (
          <Col
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <Text style={{ fontSize: "0.75rem" }}>
              {moment(item?.createdAt).format("YYYY/MM/DD HH:mm:ss")}
            </Text>
          </Col>
        );
      },
      sorter: true,
      sortDirections: ["ascend", "descend"],
    },
  ];

  return (
    <>
      <Table<CaSyncError>
        columns={columns}
        fetchQueryProps={{
          $sort: { id: -1 }
        }}
        service={caSyncErrorsService}
      />
    </>
  );
};

